import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, CircleAlert, CircleCheck, CloudUpload, Pencil, Trash2, X } from 'lucide-react';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import { noImage, UPLOAD_ACCEPTED_TYPES, UPLOAD_MAX_SIZE } from './tradeInImages.js';
import { findTakenImageNames, importLibraryImages } from './tradeInMockApi.js';

// ERP-5289 / ERP-5318 — "Import Images": bulk-upload local files into the shared Listing Image
// library. Opened from the Listing Image tab (Image Management) and from the image picker in
// the Add/Edit flow.
//
// Every file is checked before import and lands in a tab:
//   Valid    ready to import
//   Issue    name clashes with the library / another listing / another file in this batch —
//            fixable by renaming in place (PRD 2.2 "Import Rename"; the extension has to stay
//            the same)
//   Invalid  wrong format or over 1MB — can only be removed
// Only the Valid tab is imported. The image picker passes allowRename={false} — PRD 2.3: no
// import-rename there, so no Issue tab either (a clashing name is Invalid like any other file
// that can't be imported) — and its imports are recorded with upload source 'picker' (ERP-5318).

const FONT = 'Montserrat, sans-serif';

const RESULT = {
    ok: { kind: 'valid', label: 'Valid', color: '#146C43', Icon: CircleCheck },
    lib: { kind: 'issue', label: 'Image Name already exists', color: '#B02A37', Icon: CircleAlert },
    batch: { kind: 'issue', label: 'Image Name already exists', color: '#B02A37', Icon: CircleAlert },
    format: { kind: 'invalid', label: 'Unsupported image format', color: '#B02A37', Icon: CircleAlert },
    size: { kind: 'invalid', label: 'Image exceeds 1MB', color: '#B02A37', Icon: CircleAlert },
};

const TABS = [
    { key: 'valid', label: 'Valid', dot: '#28A745' },
    { key: 'issue', label: 'Issue', dot: '#FFC107' },
    { key: 'invalid', label: 'Invalid', dot: '#DC3545' },
];
const PICKER_TABS = TABS.filter(t => t.key !== 'issue');

const extOf = (name) => { const m = name.match(/\.[^.]+$/); return m ? m[0] : ''; };

let fileSeq = 0;

export default function TradeInImportImagesModal({ onClose, onImported, excludeListingId = null, zIndex = 1100, allowRename = true }) {
    const tabs = allowRename ? TABS : PICKER_TABS;
    // Without rename, a name clash can only be removed — it counts as Invalid.
    const kindOf = (st) => (!allowRename && RESULT[st].kind === 'issue' ? 'invalid' : RESULT[st].kind);
    const [files, setFiles] = useState([]);     // { id, file, name, src, invalid, st }
    const [tab, setTab] = useState('valid');
    const [editId, setEditId] = useState(null);
    const [draft, setDraft] = useState('');
    const [draftErr, setDraftErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [importError, setImportError] = useState('');
    const inputRef = useRef(null);
    // Lower-cased names known to be taken in the library or by another listing.
    const takenRef = useRef(new Set());

    // Free the object URLs this modal created.
    const filesRef = useRef(files);
    useEffect(() => { filesRef.current = files; }, [files]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => filesRef.current.forEach(f => URL.revokeObjectURL(f.src)), []);

    // A name clash depends on the whole batch (a duplicate resolves when its twin is renamed or
    // removed), so every file is rechecked after any change.
    const recheck = (list) => {
        const counts = {};
        list.forEach(f => { if (!f.invalid) counts[f.name.toLowerCase()] = (counts[f.name.toLowerCase()] || 0) + 1; });
        return list.map(f => {
            if (f.invalid) return { ...f, st: f.invalid };
            const key = f.name.toLowerCase();
            const st = takenRef.current.has(key) ? 'lib' : counts[key] > 1 ? 'batch' : 'ok';
            return { ...f, st };
        });
    };

    const addFiles = async (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length || busy) return;
        const fresh = incoming.map(file => ({
            id: `imp-${++fileSeq}`,
            file,
            name: file.name,
            src: URL.createObjectURL(file),
            invalid: !UPLOAD_ACCEPTED_TYPES.includes(file.type) ? 'format' : file.size > UPLOAD_MAX_SIZE ? 'size' : null,
        }));
        setBusy(true);
        try {
            const taken = await findTakenImageNames(fresh.filter(f => !f.invalid).map(f => f.name), excludeListingId);
            taken.forEach(n => takenRef.current.add(n.toLowerCase()));
        } finally {
            setBusy(false);
        }
        setFiles(prev => recheck([...prev, ...fresh]));
    };

    const remove = (id) => {
        const gone = files.find(f => f.id === id);
        if (gone) URL.revokeObjectURL(gone.src);
        if (editId === id) setEditId(null);
        setFiles(prev => recheck(prev.filter(f => f.id !== id)));
    };

    // Same rules as the design: name required, extension required and unchanged, unique.
    const localDraftError = (value, f) => {
        const v = value.trim();
        const ext = extOf(f.name);
        if (!v) return 'Image name is required';
        if (!extOf(v)) return `File extension is required, e.g. ${ext}`;
        if (extOf(v).toLowerCase() !== ext.toLowerCase()) return `File extension must stay ${ext}`;
        if (v.length === ext.length) return 'Image name is required';
        const lower = v.toLowerCase();
        if (takenRef.current.has(lower) || files.some(o => o.id !== f.id && !o.invalid && o.name.toLowerCase() === lower)) {
            return 'Image Name already exists';
        }
        return '';
    };

    const startEdit = (f) => { setEditId(f.id); setDraft(f.name); setDraftErr(''); };

    const saveEdit = async () => {
        const f = files.find(x => x.id === editId);
        if (!f) return;
        const value = draft.trim();
        let err = localDraftError(value, f);
        if (!err && value.toLowerCase() !== f.name.toLowerCase()) {
            setBusy(true);
            try {
                const taken = await findTakenImageNames([value], excludeListingId);
                if (taken.size) { takenRef.current.add(value.toLowerCase()); err = 'Image Name already exists'; }
            } finally {
                setBusy(false);
            }
        }
        if (err) { setDraftErr(err); return; }
        setFiles(prev => recheck(prev.map(x => (x.id === f.id ? { ...x, name: value } : x))));
        setEditId(null);
    };

    const onDraftChange = (value) => {
        setDraft(value);
        const f = files.find(x => x.id === editId);
        if (f) setDraftErr(localDraftError(value, f));
    };

    const count = (kind) => files.filter(f => kindOf(f.st) === kind).length;
    const validFiles = files.filter(f => f.st === 'ok');
    const shown = files.filter(f => kindOf(f.st) === tab);
    // Rename is for the Valid and Issue tabs of Image Management only.
    const canRename = (f) => allowRename && kindOf(f.st) !== 'invalid';
    const hasAction = shown.some(canRename);
    const gridCols = hasAction ? '65px 75px minmax(200px,1fr) 218px 68px' : '65px 75px minmax(200px,1fr) 230px';

    const doImport = async () => {
        if (!validFiles.length || busy) return;
        setBusy(true);
        setImportError('');
        let created;
        try {
            created = await importLibraryImages(validFiles.map(f => ({ file: f.file, name: f.name })), excludeListingId, allowRename ? 'import' : 'picker');
        } catch (err) {
            console.error('Image import failed:', err);
            setImportError(err.message || 'Image import failed');
            setBusy(false);
            return;
        }
        // Closing unmounts the modal, so nothing is set on this path afterwards.
        if (onImported) onImported(created);
        onClose();
    };

    const cell = (bg, h, extra) => ({
        height: h, display: 'flex', alignItems: 'center', padding: '0 10px', boxSizing: 'border-box',
        background: bg, borderBottom: '1px solid #DEE2E6', ...extra,
    });
    const headCell = { height: 45, display: 'flex', alignItems: 'center', padding: '0 10px', background: '#E9ECEF', fontWeight: 700 };
    const btn = { height: 34, minWidth: 100, padding: '0 10px', borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', cursor: 'pointer' };

    return createPortal(
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(33,37,41,0.45)' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-label="Import Images"
                style={{
                    width: 'min(1000px, calc(100vw - 40px))', height: 'min(700px, calc(100vh - 40px))', boxSizing: 'border-box',
                    display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: 12,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.12)', fontFamily: FONT, color: '#212529',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 20, borderBottom: '1px solid #CED4DA' }}>
                    <span style={{ flex: 1, color: '#000000', fontSize: 16, fontWeight: 700, lineHeight: 1 }}>Import Images</span>
                    <button type="button" onClick={onClose} title="Close" style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#212529' }}>
                        <X size={18} />
                    </button>
                </div>

                <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: 20 }}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                    />
                    <div
                        onClick={() => inputRef.current && inputRef.current.click()}
                        onDragOver={e => e.preventDefault()}
                        onDragEnter={e => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                        onDrop={e => { e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files); }}
                        style={{
                            height: 80, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                            boxSizing: 'border-box', border: `1px dashed ${isDragging ? '#212529' : '#6C757D'}`, borderRadius: 10, cursor: 'pointer',
                            background: isDragging ? '#FFFEF0' : '#FFFFFF',
                        }}
                    >
                        <CloudUpload size={24} color="#6C757D" />
                        <div style={{ display: 'flex', gap: 5, fontSize: 12 }}>
                            <span style={{ color: '#6C757D' }}>{busy ? 'Checking images…' : 'Upload Images from Local'}</span>
                            {!busy && <span style={{ color: '#0D6EFD', fontWeight: 600 }}>Browse</span>}
                        </div>
                    </div>

                    <div style={{ height: 40, flexShrink: 0, display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 10, padding: 5, boxSizing: 'border-box', background: '#E9ECEF', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                        {tabs.map(t => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => { setTab(t.key); setEditId(null); }}
                                style={{
                                    height: 30, minWidth: 88, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0 10px',
                                    boxSizing: 'border-box', background: tab === t.key ? '#FFFFFF' : 'transparent', border: 'none', borderRadius: 8,
                                    fontFamily: FONT, fontSize: 12, fontWeight: 600, color: '#212529', cursor: 'pointer', whiteSpace: 'nowrap',
                                }}
                            >
                                <span style={{ width: 7, height: 7, borderRadius: 99, background: t.dot }} />
                                {count(t.key)} {t.label}
                            </button>
                        ))}
                    </div>

                    {files.length === 0 ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 0' }}>
                            <img src={noBrandImage} alt="" style={{ width: 102, height: 102 }} />
                            <span style={{ fontSize: 20, fontWeight: 600, textAlign: 'center' }}>No Image Found</span>
                            <span style={{ color: '#6C757D', fontSize: 14, textAlign: 'center' }}>Nothing here yet.</span>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: gridCols, fontSize: 12 }}>
                            <div style={headCell} />
                            <div style={headCell}>Image</div>
                            <div style={headCell}>Image Name</div>
                            <div style={headCell}>Check Result</div>
                            {hasAction && <div style={headCell}>Action</div>}

                            {shown.map((f, i) => {
                                const bg = i % 2 ? '#F8F9FA' : '#FFFFFF';
                                const editing = editId === f.id;
                                const h = editing ? 75 : 55;
                                const r = RESULT[f.st];
                                return (
                                    <React.Fragment key={f.id}>
                                        <div style={cell(bg, h, { justifyContent: 'center', padding: 0 })}>
                                            <button type="button" title="Remove" onClick={() => remove(f.id)} style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#6C757D' }}>
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                        <div style={cell(bg, h)}>
                                            <img
                                                src={f.src}
                                                alt=""
                                                onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                                                style={{ width: 40, height: 40, objectFit: 'contain', mixBlendMode: 'darken' }}
                                            />
                                        </div>
                                        <div style={cell(bg, h, { wordBreak: 'break-all' })}>
                                            {editing ? (
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <div style={{ height: 40, minWidth: 260, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', boxSizing: 'border-box', background: '#FFFFFF', border: `1px solid ${draftErr ? '#DC3545' : '#212529'}`, borderRadius: 8 }}>
                                                        <input
                                                            autoFocus
                                                            value={draft}
                                                            onChange={e => onDraftChange(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter') saveEdit();
                                                                if (e.key === 'Escape') setEditId(null);
                                                            }}
                                                            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT, fontSize: 14, color: '#212529' }}
                                                        />
                                                        <button type="button" title="Clear" onClick={() => onDraftChange('')} style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#6C757D' }}>
                                                            <X size={12} />
                                                        </button>
                                                    </div>
                                                    {draftErr && <span style={{ padding: '0 5px', fontSize: 12, lineHeight: '15px', color: '#DC3545' }}>{draftErr}</span>}
                                                </div>
                                            ) : (
                                                <span>{f.name}</span>
                                            )}
                                        </div>
                                        <div style={cell(bg, h, { gap: 5, color: r.color, fontWeight: 700 })}>
                                            <r.Icon size={18} style={{ flexShrink: 0 }} />
                                            <span>{r.label}</span>
                                        </div>
                                        {hasAction && (
                                            <div style={cell(bg, h, { justifyContent: 'center', gap: 10, padding: 0 })}>
                                                {editing ? (
                                                    <>
                                                        <button type="button" title="Save" disabled={busy} onClick={saveEdit} style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#146C43' }}>
                                                            <Check size={18} />
                                                        </button>
                                                        <button type="button" title="Cancel" onClick={() => setEditId(null)} style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#6C757D' }}>
                                                            <X size={18} />
                                                        </button>
                                                    </>
                                                ) : canRename(f) && (
                                                    <button type="button" title="Rename" onClick={() => startEdit(f)} style={{ display: 'flex', padding: 8, border: 'none', borderRadius: 8, background: 'none', cursor: 'pointer', color: '#212529' }}>
                                                        <Pencil size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: 20, borderTop: '1px solid #CED4DA' }}>
                    {importError && (
                        <span style={{ marginRight: 'auto', color: '#B02A37', fontSize: 12, fontWeight: 600, whiteSpace: 'pre-line' }}>{importError}</span>
                    )}
                    {files.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6C757D', fontSize: 12, fontWeight: 600 }}>
                            <CircleAlert size={18} />
                            <span>
                                {validFiles.length === files.length
                                    ? `Import ${files.length} image${files.length === 1 ? '' : 's'}`
                                    : `Import ${validFiles.length} valid image${validFiles.length === 1 ? '' : 's'}`}
                            </span>
                        </div>
                    )}
                    <button type="button" onClick={onClose} style={{ ...btn, border: '1px solid #212529', background: '#FFFFFF', color: '#212529' }}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={doImport}
                        disabled={!validFiles.length || busy}
                        style={{
                            ...btn,
                            ...(validFiles.length && !busy
                                ? { border: '1px solid #212529', background: '#212529', color: '#FFFFFF' }
                                : { border: '1px solid #CED4DA', background: '#E9ECEF', color: '#6C757D', cursor: 'not-allowed' }),
                        }}
                    >
                        Import
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
